import { Button, Table, TableCol, TablePagination, TableSort } from 'zens-storybook'
import React from 'react'
import { StyleSheet, ViewProps } from 'react-native'
import { useTranslation } from 'react-i18next'
import { AccessType, Location, useLocations } from 'zens-store'

import { usePermissions } from '@dms/hook'

import { Link } from 'zens-router'

import { TablePaginated } from '../table'
import { Panel } from '../layout'

import LocationEmpty from './empty'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
})

interface Data {
  data: Location
  onPress?: () => void
}

interface Props extends ViewProps {
  companyId: number
  pagination?: TablePagination
  onPaginationChange?: (pagination: TablePagination) => void
  sort?: TableSort
  onSortChange?: (sort: TableSort) => void
}
const LocationTable = (props: Props) => {
  const {
    companyId,
    pagination = { page: 0, size: 10 },
    onPaginationChange,
    sort,
    onSortChange,
    style,
    ...otherProps
  } = props
  const { t } = useTranslation()
  const locations = useLocations({ companyId })
  const { hasPermission } = usePermissions(companyId)

  const data: Data[] =
    locations.data?.map((location) => ({
      data: location,
      href: `/companies/${companyId}/locations/${location.id}`,
    })) || []

  const cols: TableCol<Location>[] = [
    {
      key: 'name',
      title: t('Name'),
    },
    {
      key: 'numberOfBuildings',
      title: t('Buildings'),
    },
    {
      key: 'numberOfWorkspaces',
      title: t('Workspaces'),
    },
  ]
  return (
    <Panel
      style={[style, styles.container]}
      title={t('Locations')}
      right={
        (hasPermission(AccessType.Create, 'location') && locations.data?.length && (
          <Link href={`/companies/${companyId}/locations/add`} asChild>
            <Button title={t('Add location')} left="plus" testID="LocationList.Add" />
          </Link>
        )) ||
        undefined
      }
      {...otherProps}
    >
      <TablePaginated
        rows={data}
        cols={cols}
        pagination={pagination}
        sort={sort}
        onSortChange={onSortChange}
        count={data.length}
        renderCount={() =>
          t('{{page}} of {{numberOfPages}}', {
            page: pagination.page + 1,
            numberOfPages: Math.ceil(data.length / pagination.size),
          })
        }
        sizeLabel={t('Locations per page')}
        maxSize={1000}
        onPaginationChange={onPaginationChange}
        ListEmptyComponent={<LocationEmpty companyId={companyId} />}
      />
    </Panel>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()
  const cols: TableCol<Location>[] = [
    {
      key: 'name',
      title: t('Name'),
    },
    {
      key: 'numberOfBuildings',
      title: t('Buildings'),
    },
    {
      key: 'numberOfWorkspaces',
      title: t('Workspaces'),
    },
  ]
  return (
    <Panel title={t('Locations')} right={<Button.Skeleton />} {...props}>
      <Table.Skeleton cols={cols} hasAccessory />
    </Panel>
  )
}
LocationTable.Skeleton = Skeleton

export default LocationTable
